import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import i18n from '@pureartisan/simple-i18n'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import shallow from 'zustand/shallow'
import Papa from 'papaparse'

import useStore from './../store'
import { theme } from './../theme'
import { DATA_FILES } from './../../../../constants/map'
import _ from 'lodash'
import { setUtilsRemoteData } from './../utils'

// TODO:
// - Error notification if data loading fails.

const useLoaderStyles = makeStyles({
  root: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100vh',
    width: '100vw',
    backgroundColor: theme.palette.common.white,
    zIndex: 5000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'top 1000ms ease-in-out',
  },
  dataLoaded: {
    top: `-100vh`,
  },
  content: {
    display: 'block',
    width: '90%',
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
  },
  hideContent: {
    display: 'none',
  },
  '@global': {
    '@keyframes fadeIn': {
      '0%': {
        opacity: 0,
      },
      '100%': {
        opacity: 1,
      },
    },
  },
  dots: {
    animationName: 'fadeIn',
    animationDuration: '1s',
    animationIterationCount: 'infinite',
  },
  text: {
    fontFamily: 'Fira Sans !important',
    color: theme.extras.variables.colors.darkGray,
  },
  supportedBrowsers: {
    margin: '2rem auto',
    display: 'block',
    fontFamily: 'Fira Sans !important',
    color: theme.extras.variables.colors.darkGray,
    width: '90%',
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
  },
})

const DataLoaderContent = ({ ...props }) => {
  // console.log('DataLoaderContent, ', variables)
  // Values from store.
  const {
    dataLoadedPercent,
    allDataLoaded,
    unsupportedBrowser,
    browser,
  } = useStore(
    state => ({
      dataLoadedPercent: state.dataLoadedPercent,
      allDataLoaded: state.allDataLoaded,
      unsupportedBrowser: state.unsupportedBrowser,
      browser: state.browser,
    }),
    shallow,
  )

  // NOTE: for debugging only!
  // const allDataLoaded = true

  // Hack, hide this for a bit to avoid flashing empty string var.
  const [showContent, setShowContent] = useState(false)
  setTimeout(() => {
    setShowContent(true)
  }, 500)

  const hasBrowserSupport = useMemo(() => {
    return browser && !unsupportedBrowser
  }, [browser, unsupportedBrowser])

  // console.log('browser, ', browser)

  const styles = useLoaderStyles()

  return (
    <Box
      className={clsx(styles.root, {
        [styles.dataLoaded]: allDataLoaded,
      })}
    >
      {/* hideContent Hack, hide this for a bit to avoid flashing empty string var. */}
      <Box
        className={clsx(styles.content, {
          [styles.hideContent]: !showContent,
        })}
      >
        {hasBrowserSupport && (
          <>
            <Typography
              variant="h4"
              gutterBottom
              className={clsx(styles.text)}
            >
              {i18n.translate(`MAP_LOADING_DATA`)}
              <Box component="span" className={styles.dots}>
                .
              </Box>
              <Box
                component="span"
                style={{
                  animationDelay: '200ms',
                }}
                className={clsx(styles.dots)}
              >
                .
              </Box>
              <Box
                component="span"
                style={{
                  animationDelay: '400ms',
                }}
                className={clsx(styles.dots)}
              >
                .
              </Box>
            </Typography>
            <LinearProgress
              variant="determinate"
              value={dataLoadedPercent}
            />
          </>
        )}
        {!hasBrowserSupport && (
          <>
            <Typography
              variant="h4"
              gutterBottom
              className={clsx(styles.text)}
            >
              {i18n.translate(`MAP_UNSUPPORTED_BROWSER`, {
                name:
                  !!browser && !!browser.name
                    ? browser.name
                    : '',
                version:
                  !!browser && !!browser.version
                    ? browser.version
                    : '',
              })}
            </Typography>
            <div
              className={clsx(
                'supported-browsers',
                styles.supportedBrowsers,
              )}
              dangerouslySetInnerHTML={{
                __html: i18n.translate(
                  'MAP_SUPPORTED_BROWSERS',
                ),
              }}
            ></div>
          </>
        )}
      </Box>
    </Box>
  )
}

const DataLoader = ({ ...props }) => {
  // console.log('DataLoader()')
  // Values from store.
  const {
    initialStateSetFromHash,
    setStoreValues,
    setRemoteJson,
    setLang,
    activeYear,
    activeGeoType,
    dataVersion,
    unsupportedBrowser,
    browser,
  } = useStore(
    state => ({
      initialStateSetFromHash:
        state.initialStateSetFromHash,
      // Generic store value setter.
      setStoreValues: state.setStoreValues,
      // Special setter to merge loaded json into existing obj.
      setRemoteJson: state.setRemoteJson,
      setLang: state.setLang,
      activeYear: state.activeYear,
      activeGeoType: state.activeGeoType,
      dataVersion: state.dataVersion,
      unsupportedBrowser: state.unsupportedBrowser,
      browser: state.browser,
    }),
    shallow,
  )

  const getPath = el => {
    // example path: https://ddk-source.s3.amazonaws.com/proc/4.0.6/gzip/zips_pop17.csv.gz
    let s3Path = process.env.AWS_ENDPOINT
    const fixedVersion = el.geoDependent
      ? _.get(el.fixedVersion, activeGeoType)
      : el.fixedVersion
    // NOTE: not all resources are "backwards compatible"; if a resource isn't available for an
    // older data version, it breaks. so for now use app- rather than user-provided data version
    s3Path += fixedVersion || dataVersion
    s3Path += '/'
    if (!el.unzipped) s3Path += 'gzip/'
    if (el.geoDependent) s3Path += `${activeGeoType}_`
    s3Path += el.filename
    if (el.yearDependent) s3Path += activeYear
    s3Path += `.${el.ext}`
    if (!el.unzipped) s3Path += '.gz'
    // console.log({ el, s3Path })
    return s3Path
  }

  // Fetch each file, and update the objects you need to update.
  // const files = DATA_FILES
  // Counter for loaded files.
  let loadedCount = 0
  // Process a downloaded file outside of sync request.
  const processFile = (el, response) => {
    if (el.type === 'data' && el.ext === 'json') {
      // console.log('parsing data.')
      let obj = {}
      const data = JSON.parse(response)
      obj[el.id] = {
        type: el.type,
        data,
      }
      // obj[el.id] = JSON.parse(xhr.responseText)
      // console.log('json file parsed, ', el.id, obj)
      setRemoteJson(obj)

      // load remote data needed by utils so that other util methods don't need to pass it in
      if (el.useInUtils) {
        setUtilsRemoteData({ data, id: el.id })
      }
    }
    // Parse CSV into JSON before sticking it into the store.
    if (el.type === 'data' && el.ext === 'csv') {
      // console.log('parsing csv: ', el)
      let obj = {}
      // Parse asynchronously using papaparse to prevent UI from locking up.
      // console.log('parsing CSV file, ', el.id)
      // console.log(browser.isIos)
      Papa.parse(response, {
        header: true,
        // papaparse web worker crashes iOS browsers
        worker: !browser.isIos,
        complete: function (results) {
          // console.log('file parsed, ', el.id, results)
          obj[el.id] = {
            type: el.type,
            data: results.data,
          }
          // console.log('csv file parsed, ', el.id)
          setRemoteJson(obj)
        },
      })
    }
    // if (el.type === 'dict') {
    //   // Merge loaded dictionary values with existing dictionary.
    //   // const strings = { en_US: JSON.parse(response) }
    //   const strings = { en_US: {} }
    //   // console.log('lang file parsed, ', el.id)
    //   console.log('strings,', strings)
    //   setLang(strings)
    // }
    // console.log('remoteJSON, ', remoteJson)
  }

  const loadYearFiles = () => {
    // console.log('loadYearFiles')
    // Load each file.
    // Set each file to the store.
    // Update loaded percent.
    // Update overall loading tracking.
    const yearFiles = DATA_FILES.filter(el => {
      return el.yearDependent === 1
    })
    yearFiles.forEach((el, i) => {
      const xhr = new XMLHttpRequest()
      const path = getPath(el)
      // console.log({ path, el })
      xhr.open('GET', path, true)
      xhr.onload = function (e) {
        // console.log('loaded, ', xhr)
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            // console.log(`year file ${el.id} loaded.`)
            processFile(el, xhr.responseText)
          } else {
            // console.error(xhr.statusText)
            // Flag something failed.
            setStoreValues({
              dataLoaderFailed: true,
            })
          }
        }
      }
      xhr.onerror = function (e) {
        // console.error(xhr.statusText)
        // Flag something failed.
        setStoreValues({
          dataLoaderFailed: true,
        })
      }
      xhr.send(null)
    })
  }

  const loadGeoFiles = () => {
    const geoFiles = DATA_FILES.filter(el => {
      return el.geoDependent === 1
    })
    geoFiles.forEach((el, i) => {
      const xhr = new XMLHttpRequest()
      const path = getPath(el)
      xhr.open('GET', path, true)
      xhr.onload = function (e) {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            processFile(el, xhr.responseText)
          } else {
            // Flag something failed.
            setStoreValues({
              dataLoaderFailed: true,
            })
          }
        }
      }
      xhr.onerror = function (e) {
        // Flag something failed.
        setStoreValues({
          dataLoaderFailed: true,
        })
      }
      xhr.send(null)
    })
  }

  const loadFiles = () => {
    // console.log('loadFiles')
    // Load each file.
    // Set each file to the store.
    // Update loaded percent.
    // Update overall loading tracking.
    DATA_FILES.forEach((el, i) => {
      const xhr = new XMLHttpRequest()
      const path = getPath(el)
      // console.log('path, ', path)
      xhr.open('GET', path, true)
      xhr.onload = function (e) {
        // console.log('loaded, ', xhr)
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            // Increment counter for loaded files.
            loadedCount++
            // console.log(
            //   'file loaded ',
            //   el.id,
            //   (loadedCount / files.length) * 100,
            // )
            setStoreValues({
              dataLoadedPercent:
                (loadedCount / DATA_FILES.length) * 100,
              allDataLoaded:
                loadedCount === DATA_FILES.length
                  ? true
                  : false,
            })
            processFile(el, xhr.responseText)
          } else {
            // console.error(xhr.statusText)
            // Flag something failed.
            setStoreValues({
              dataLoaderFailed: true,
            })
          }
        }
      }
      xhr.onerror = function (e) {
        // console.error(xhr.statusText)
        // Flag something failed.
        setStoreValues({
          dataLoaderFailed: true,
        })
      }
      xhr.send(null)
    })
  }

  useEffect(() => {
    if (!initialStateSetFromHash) {
      return
    } else {
      // console.log('initial state set.')
      // console.log('activeYear, ', activeYear)
      // Only proceed if browser is supported.
      if (!!browser && !unsupportedBrowser) {
        loadFiles()
      }
    }
  }, [initialStateSetFromHash])

  // reload year-dependent files when year changes
  useEffect(() => {
    if (!initialStateSetFromHash) {
      return
    } else {
      // console.log('activeYear, ', activeYear)
      // Only proceed if browser is supported.
      if (!!browser && !unsupportedBrowser) {
        loadYearFiles()
      }
    }
  }, [activeYear])

  // reload geo-dependent files when geo changes
  useEffect(() => {
    if (!initialStateSetFromHash) {
      return
    } else {
      // Only proceed if browser is supported.
      if (!!browser && !unsupportedBrowser) {
        loadGeoFiles()
      }
    }
  }, [activeGeoType])

  return <DataLoaderContent />
}

export default DataLoader
